import { Injectable } from '@angular/core';
import { State } from '../models/state.model';
import { SimpleGlobal } from 'ng2-simple-global';
import { ApiService } from './api.service';
import { Observable } from 'rxjs/Observable';
import { District } from '../models/district.model';
import { Company } from '../models/company.model';
import 'rxjs/add/operator/map';
import 'rxjs/add/operator/catch';
import { URLSearchParams } from '@angular/http';
import { NotificationsService } from 'angular2-notifications';
import { User } from '../models/user.model';
import { IMultiSelectSettings } from "angular-2-dropdown-multiselect";
import { AuthService } from "./auth.service";

@Injectable()
export class CommonService {

    // Settings configuration
    public multiSelectSettings: IMultiSelectSettings = {
        enableSearch: true,
        checkedStyle: 'fontawesome',
        buttonClasses: 'multiselect-items btn btn-default',
        dynamicTitleMaxItems: 1,
        displayAllSelectedText: false,
        maxHeight: '300px',
    };


    constructor(
        public global: SimpleGlobal,
        private api: ApiService,
        public notifications: NotificationsService,
        public auth: AuthService
    ) {
    }

    populateGlobalVariables() {
        this.populateStates();
        this.populateDistricts();
        this.populateIdentityDocumentTypes();

        this.global['multi_select_setting'] = this.multiSelectSettings;
    }

    /**
     * API method to update verification status of multiple verifications
     *
     * @param verification : which verification, identity,address,icr etc.
     * @param verificationIds : array of verification ids to be updated.
     * @param statusId : verification status id to be updated to.
     */
    changeVerificationStatus(verification: string, verificationIds: number[], statusId: number): Observable<any[]> {
        return this.api.post('common/change-verification-status', {
            verification: verification,
            ids: verificationIds,
            status_id: statusId
        });
    }

    /**
     * API method to update verification status of multiple verifications
     *
     * @param verification : which verification, identity,address,icr etc.
     * @param verificationIds : array of verification ids to be updated.
     * @param vendorId : verification status id to be updated to.
     */
    changeVendorId(verification: string, verificationIds: number[], vendorId: number): Observable<any[]> {
        return this.api.post('common/change-vendor-id', {
            verification: verification,
            ids: verificationIds,
            vendor_id: vendorId
        });
    }

    updateSentToVendorOn(verification: string, verificationIds: number[], sentToVendorOn: string): Observable<any[]> {
        return this.api.post('common/change-sent-to-vendor-on', {
            verification: verification,
            ids: verificationIds,
            sent_to_vendor_on: sentToVendorOn
        });
    }

    public displayValidationErrors(errors: any[]): void {

        const fields = [];
        const messages = [];

        Object.keys(errors).forEach(key => {
            if (errors[key] !== '') {
                messages.push(errors[key]);
                fields.push(key);
            }

            this.notifications.error(key, errors[key]);
        });
    }

    getVerificationStats(verification: string, params: URLSearchParams = new URLSearchParams()): Observable<any[]> {
        return this.api.get(verification + '/stats', params);
    }

    getLotsByCompanyId(companyId: number): Observable<any[]> {
        return this.api.get('lots/list?company_id=' + companyId);
    }

    getCompanies(): Observable<Company[]> {
        return this.api.get('companies/list');
    }

    getStates(): Observable<State[]> {
        return this.api.get('common/states');
    }

    getDistricts(): Observable<District[]> {
        return this.api.get('common/districts');
    }

    getAuthorities(): Observable<District[]> {
        return this.api.get('common/authorities');
    }

    getStatuses(): Observable<any[]> {
        return this.api.get('common/statuses');
    }

    getUsers(): Observable<User[]> {
        return this.api.get('common/users')
    }

    getVendors(verification: string): Observable<User[]> {
        return this.api.get('common/vendors?verification=' + verification)
    }

    getCountries(): Observable<User[]> {
        return this.api.get('common/countries')
    }

    getLanguages(): Observable<User[]> {
        return this.api.get('common/languages')
    }

    private populateUsers() {
        this.getUsers().subscribe(
            response => this.global['users'] = response
        )

    }

    private populateCompanies(): void {
        this.getCompanies().subscribe(
            response => this.global['companies'] = response
        );
    }

    private populateStates() {
        this.getStates().subscribe(
            response => this.global['states'] = response
        );
    }

    private populateDistricts() {
        this.getDistricts().subscribe(
            response => this.global['districts'] = response
        );
    }

    populateAuthorities() {
        this.getAuthorities().subscribe(
            response => this.global['authorities'] = response
        );
    }

    private populateStatuses() {

        this.global['statues'] = {
            'identity': [],
            'address': [],
            'icr': [],
            'education': [],
            'employment': [],
            'reference': [],
            'pcc': [],
            'document': [],
        };

        this.getStatuses().subscribe(
            response => this.global['statuses'] = response
        );
    }

    private populateIdentityDocumentTypes() {
        this.global['identity_types'] = [
            {
                id: 3,
                name: 'Aadhaar Card'
            },
            {
                id: 4,
                name: 'Driving Licence'
            },
            {
                id: 2,
                name: 'PAN Card'
            },
            {
                id: 1,
                name: 'Voter ID Card'
            },
            {
                id: 5,
                name: 'Passport'
            },
            {
                id: 0,
                name: 'No Document'
            },
        ]
    }

    private populateDocumentTypes() {
        this.global['document_types'] = [
            {
                id: 0,
                name: 'No Document'
            },
            {
                id: 1,
                name: 'Vehile RC'
            },
            {
                id: 2,
                name: 'All India Permit'
            },
            {
                id: 3,
                name: 'Contract Carriage Permit'
            },
            {
                id: 4,
                name: 'PSV Badge'
            },
        ]
    }

    private populateEmploymentType() {
        this.global['employment_type'] = [
            {
                id: 0,
                name: 'Current Employment'
            },
            {
                id: 1,
                name: 'Past Employment'
            },
            {
                id: 2,
                name: 'No Employment'
            }
        ]
    }

    private populateMethodology() {
        this.global['methodology'] = [
            {
                id: 0,
                name: 'Verbal'
            },
            {
                id: 1,
                name: 'Online'
            },
            {
                id: 2,
                name: 'Offline'
            },
            {
                id: 3,
                name: 'Vendor'
            },
        ]
    }

    private populateCountries() {
        this.getCountries().subscribe(
            response => this.global['countries'] = response
        );
    }

    deleteMultiple(ids: number[], verification: string): Observable<any[]> {
        return this.api.post('common/delete-verifications', {
            ids: ids,
            verification: verification
        })
    }

    changestatus(ids: number[], verification: string, status_id: number): Observable<any[]> {
        return this.api.post('common/change-verification-status', {
            ids: ids,
            verification: verification,
            status_id: status_id
        })
    }

    saveCroppedImage(image: string, verificationId, proofType, verificationType): Observable<any> {
        return this.api.post('identity/' + verificationId + '/save-image', {
            image_data: image,
            image_type: proofType,
            verification: verificationType
        });
    }

    getDataForExcel(verification: string, params: URLSearchParams = new URLSearchParams()): Observable<any[]> {
        return this.api.get('common/downloadExcel?verification=' + verification, params);
    }

    getPincodeDetails(pincode: string): Observable<any[]> {
        return this.api.post('common/pincodes', {
            pincode: pincode
        });
    }

    getAllEmploymentCompanies(): Observable<any[]> {
        return this.api.get('employment/all-companies');
    }

    getAllInstitutes(): Observable<any[]> {
        return this.api.get('education/all-institutes');
    }

    getClientRoutes(): Observable<any[]> {
        return this.api.get('common/client-routes');
    }

    deleteVerificationDocument(data): Observable<any []> {
        return this.api.post('common/delete-document', data);
    }

}



// WEBPACK FOOTER //
// /home/forge/dev-client-app.instaveritas.com/releases/20231127121751/src/app/core/services/common.service.ts